import { chain } from '@leprecoins/effector';
import { createEvent, createStore, Event, EventCallable, sample, Store } from 'effector';
import { not, readonly } from 'patronum';

export type FieldValue = number | string | number[];

export type FieldType = 'number' | 'string' | 'array' | 'select';

export type DynamicRule = {
	name: string;
	validate: (value: FieldValue) => boolean;
};

export type NumberChangePayload = {
	key: string;
	value: Extract<FieldValue, number>;
	type: Extract<FieldType, 'number'>;
};

export type StringChangePayload = {
	key: string;
	value: Extract<FieldValue, string>;
	type: Extract<FieldType, 'string'>;
};

export type NumberArrayChangePayload = {
	key: string;
	value: Extract<FieldValue, number[]>;
	type: Extract<FieldType, 'array'>;
};

export type SelectChangePayload = {
	key: string;
	value: Extract<FieldValue, number>;
	type: Extract<FieldType, 'select'>;
};

export type ChangePayload =
	| NumberChangePayload
	| StringChangePayload
	| NumberArrayChangePayload
	| SelectChangePayload;

export type NumberFieldPayload = {
	key: string;
	value: Extract<FieldValue, number>;
	type: Extract<FieldType, 'number'>;
	rules?: DynamicRule[];
};

export type StringFieldPayload = {
	key: string;
	value: Extract<FieldValue, string>;
	type: Extract<FieldType, 'string'>;
	rules?: DynamicRule[];
};

export type NumberArrayFieldPayload = {
	key: string;
	value: Extract<FieldValue, number[]>;
	type: Extract<FieldType, 'array'>;
	rules?: DynamicRule[];
};

export type SelectFieldPayload = {
	key: string;
	value: Extract<FieldValue, number>;
	type: Extract<FieldType, 'select'>;
	options: { value: string; label: string }[];
	rules?: DynamicRule[];
};

export type FieldPayload =
	| NumberFieldPayload
	| StringFieldPayload
	| NumberArrayFieldPayload
	| SelectFieldPayload;

type DynamicFieldBase = {
	key: string;
	disabled: boolean;
	errors: string[];
	dirty: boolean;
};

export type NumberDynamicField = {
	initialValue: Extract<FieldValue, number>;
	value: Extract<FieldValue, number>;
	type: Extract<FieldType, 'number'>;
	rules: DynamicRule[];
} & DynamicFieldBase;

export type StringDynamicField = {
	initialValue: Extract<FieldValue, string>;
	value: Extract<FieldValue, string>;
	type: Extract<FieldType, 'string'>;
	rules: DynamicRule[];
} & DynamicFieldBase;

export type NumberArrayDynamicField = {
	initialValue: Extract<FieldValue, number[]>;
	value: Extract<FieldValue, number[]>;
	type: Extract<FieldType, 'array'>;
	rules: DynamicRule[];
} & DynamicFieldBase;

export type SelectDynamicField = {
	initialValue: Extract<FieldValue, number>;
	value: Extract<FieldValue, number>;
	type: Extract<FieldType, 'select'>;
	options: { value: string; label: string }[];
	rules: DynamicRule[];
} & DynamicFieldBase;

export type DynamicField =
	| NumberDynamicField
	| StringDynamicField
	| NumberArrayDynamicField
	| SelectDynamicField;

export type DynamicForm = {
	$fields: Store<Record<string, DynamicField>>;
	$fieldsArray: Store<DynamicField[]>;
	$valid: Store<boolean>;
	$invalid: Store<boolean>;
	$dirty: Store<boolean>;
	$pristine: Store<boolean>;
	addField: EventCallable<FieldPayload>;
	addFields: EventCallable<FieldPayload[]>;
	removeField: EventCallable<string>;
	removeAllFields: EventCallable<void>;
	change: EventCallable<ChangePayload>;
	enable: EventCallable<string>;
	disable: EventCallable<string>;
	setActive: EventCallable<{ key: string; value: boolean }>;
	reset: EventCallable<void>;
	validate: EventCallable<void>;
	submit: EventCallable<void>;
	submitted: Event<void>;
	'@@unitShape': () => {
		fields: Store<Record<string, DynamicField>>;
		fieldsArray: Store<DynamicField[]>;
		valid: Store<boolean>;
		invalid: Store<boolean>;
		dirty: Store<boolean>;
		pristine: Store<boolean>;
		addField: EventCallable<FieldPayload>;
		addFields: EventCallable<FieldPayload[]>;
		removeField: EventCallable<string>;
		removeAllFields: EventCallable<void>;
		change: EventCallable<ChangePayload>;
		enable: EventCallable<string>;
		disable: EventCallable<string>;
		setActive: EventCallable<{ key: string; value: boolean }>;
		submit: EventCallable<void>;
		reset: EventCallable<void>;
		validate: EventCallable<void>;
	};
};

const getFieldRules = (field: FieldPayload): DynamicRule[] => {
	switch (field.type) {
		case 'number':
			return [{ name: 'greater-zero', validate: (value) => (value as number) > 0 }];
		case 'string':
			return [{ name: 'not-empty', validate: (value) => (value as string).length > 0 }];
		case 'select':
			return [];
		default:
			return [{ name: 'not-empty', validate: (value) => (value as number[]).length > 0 }];
	}
};

export const createDynamicFormModel = (): DynamicForm => {
	const $fields = createStore<Record<string, DynamicField>>({});

	const $fieldsArray = $fields.map((fields) => Object.values(fields));

	const $invalid = $fields.map((fields) =>
		Object.values(fields).some((field) => field.errors.length > 0),
	);

	const $valid = not($invalid);

	const $dirty = $fields.map((fields) => Object.values(fields).some((field) => field.dirty));

	const $pristine = not($dirty);

	const addField = createEvent<FieldPayload>();

	const addFields = createEvent<FieldPayload[]>();

	const removeField = createEvent<string>();

	const removeAllFields = createEvent<void>();

	const change = createEvent<ChangePayload>();

	const setActive = createEvent<{ key: string; value: boolean }>();

	const enable = createEvent<string>();

	const disable = createEvent<string>();

	const submit = createEvent<void>();

	const submitted = createEvent<void>();

	const validate = createEvent<void>();

	const reset = createEvent<void>();

	chain(removeAllFields, $fields.reinit);

	sample({
		clock: removeField,
		source: $fields,
		fn: (fields, key) => {
			const copyFields = { ...fields };

			delete copyFields[key];

			return copyFields;
		},
		target: $fields,
	});

	sample({
		clock: addField,
		source: $fields,
		fn: (fields, fieldPayload) => {
			const copyFields = { ...fields };

			copyFields[fieldPayload.key] = {
				...fieldPayload,
				initialValue: fieldPayload.value,
				disabled: false,
				errors: [],
				dirty: false,
				rules: [...getFieldRules(fieldPayload), ...(fieldPayload.rules ?? [])],
				...(fieldPayload.type === 'select' ? { options: fieldPayload.options } : {}),
			} as DynamicField;

			return copyFields;
		},
		target: $fields,
	});

	sample({
		clock: addFields,
		source: $fields,
		fn: (fields, fieldPayloads) => {
			const newFields = fieldPayloads.reduce(
				(acc, fieldPayload) => {
					acc[fieldPayload.key] = {
						...fieldPayload,
						initialValue: fieldPayload.value,
						disabled: false,
						errors: [],
						dirty: false,
						rules: [...getFieldRules(fieldPayload), ...(fieldPayload.rules ?? [])],
						...(fieldPayload.type === 'select' ? { options: fieldPayload.options } : {}),
					} as DynamicField;

					return acc;
				},
				{} as Record<string, DynamicField>,
			);

			return { ...fields, ...newFields };
		},
		target: $fields,
	});

	sample({
		clock: change,
		source: $fields,
		filter: (fields, { key }) => !fields[key].disabled,
		fn: (fields, { key, value }) => {
			const copyFields = { ...fields };

			let dirty = false;

			if (copyFields[key].type === 'array') {
				dirty =
					!(copyFields[key].initialValue as number[]).every((item) =>
						(value as number[]).includes(item),
					) || copyFields[key].initialValue.length !== (value as number[]).length;
			} else dirty = copyFields[key].initialValue !== value;

			copyFields[key] = {
				...copyFields[key],
				value,
				dirty,
				errors: [],
			} as DynamicField;

			return copyFields;
		},
		target: $fields,
	});

	sample({
		clock: enable,
		source: $fields,
		fn: (fields, key) => {
			const copyFields = { ...fields };

			copyFields[key] = {
				...copyFields[key],
				disabled: false,
			};

			return copyFields;
		},
		target: $fields,
	});

	sample({
		clock: disable,
		source: $fields,
		fn: (fields, key) => {
			const copyFields = { ...fields };

			copyFields[key] = {
				...copyFields[key],
				disabled: true,
			};

			return copyFields;
		},
		target: $fields,
	});

	sample({
		clock: setActive,
		source: $fields,
		fn: (fields, { key, value }) => {
			const copyFields = { ...fields };

			copyFields[key] = {
				...copyFields[key],
				disabled: !value,
			};

			return copyFields;
		},
		target: $fields,
	});

	sample({
		clock: reset,
		source: $fields,
		fn: (fields) =>
			Object.keys(fields).reduce(
				(acc, key) => ({ ...acc, [key]: { ...fields[key], value: fields[key].initialValue } }),
				{},
			),
		target: $fields,
	});

	chain(submit, validate);

	sample({
		clock: validate,
		source: $fields,
		fn: (fields) =>
			Object.keys(fields).reduce((acc, key) => {
				const errors = fields[key].rules.reduce<string[]>((errorsAcc: string[], rule) => {
					if (!rule.validate(fields[key].value)) return [...errorsAcc, rule.name];

					return errorsAcc;
				}, [] as string[]);

				return { ...acc, [key]: { ...fields[key], errors } };
			}, {}),
		target: $fields,
	});

	sample({
		clock: validate,
		filter: $valid,
		target: submitted,
	});

	return {
		$fields: readonly($fields),
		$valid,
		$invalid,
		$dirty,
		$pristine,
		$fieldsArray,
		addField,
		addFields,
		removeField,
		removeAllFields,
		change,
		enable,
		disable,
		setActive,
		reset,
		submit,
		validate,
		submitted,
		'@@unitShape': () => ({
			fields: readonly($fields),
			fieldsArray: $fieldsArray,
			valid: $valid,
			invalid: $invalid,
			dirty: $dirty,
			pristine: $pristine,
			addField,
			addFields,
			removeField,
			removeAllFields,
			change,
			enable,
			disable,
			setActive,
			submit,
			reset,
			validate,
			submitted,
		}),
	};
};
